// src/services/pftImportGroups.js
//
// Turns extracted PFTs (one `pft_imports` doc per test, written by the
// extraction job) into patient cards for the PFT Uploads page. Firebase-free and
// tested (pftImportGroups.test.js).
//
// One import doc =
//   {
//     id, file (bucket path), fileName, report (1-based, within the PDF),
//     patient: { name, dob, mrn (as printed), mrn9, mrnIssue, sex, phone, address },
//     test:    { date, ...PFT values (keys from PFT_TEST_FIELDS) },
//     review:  [{ field, reason, readings? }]   what the job was unsure about
//     reviewed:{ [field]: { by, at } }          what a person has checked
//     status:  "new" | "moved" | "merged" | "dismissed",
//     patientId?, runId, extractedAt
//   }
//
// Cards: every test for the same patient goes on one card, across PDFs and
// years. Same patient = same 9-digit MRN, or (when an MRN is missing) the same
// name and DOB. Two PDFs with DIFFERENT MRNs are never joined, even with the
// same name and DOB. One PDF holding two patients gives two cards.

import { nameTokens, normalizeDob, sameMrnNumber } from "./patientDuplicateMatch";
import { normalizeMrn9 } from "./mrn";
import { PFT_TEST_VALUE_KEYS, testDateKey, visibleTestsNewestFirst } from "./pftTests";
import { parseSmoking } from "./smoking";
import { surveyOf } from "./pftSurvey";
import { softChecksOf } from "./pftSoftChecks";

const blank = (v) => v === undefined || v === null || String(v).trim() === "";

export const importMrn9 = (imp) => {
  const p = (imp && imp.patient) || {};
  return p.mrn9 || normalizeMrn9(p.mrn).mrn9;
};

// Name + DOB key, independent of word order ("SMITH, JOHN" = "John Smith").
export const nameDobKey = (imp) => {
  const p = (imp && imp.patient) || {};
  const tokens = nameTokens(p.name).sort();
  const dob = normalizeDob(p.dob);
  return tokens.length && dob ? `${tokens.join(" ")}|${dob}` : "";
};

// The same report in two different files (a re-scan, a PDF saved twice): same date,
// IDENTICAL values, AND the same name and DOB printed on it. Shown and added once,
// remembering every PDF (`alsoIn`). Anything that differs keeps them apart, so neither a
// genuinely different test nor a report printed under another name is hidden.
const whoKey = (t) => `${nameTokens(t.nameOnReport).sort().join(" ")}|${normalizeDob(t.dobOnReport)}`;
const sameReportKey = (t) =>
  `${whoKey(t)}|${testDateKey(t) || String(t.date || "").trim()}|${PFT_TEST_VALUE_KEYS.filter((k) => k !== "date")
    .map((k) => String(t[k] ?? "").trim())
    .join("\u0001")}`;

/** Tests (newest first) with same-report copies folded into one, listed in `alsoIn`. */
export const collapseSameReports = (tests = []) => {
  const out = [];
  const byKey = new Map();
  for (const t of tests) {
    const k = sameReportKey(t);
    const first = byKey.get(k);
    if (first) {
      first.alsoIn = [...(first.alsoIn || []), { importId: t.importId, file: t.file, fileName: t.fileName }];
      continue;
    }
    const copy = { ...t };
    byKey.set(k, copy);
    out.push(copy);
  }
  return out;
};

/** An import as a dated test on a patient. */
export const importToTest = (imp) => {
  // What the report itself prints (the extraction's reading, even if staff corrected the
  // card's details since), kept with the test as provenance.
  const printed = imp.extractedPatient || imp.patient || {};
  return {
    ...(imp.test || {}),
    id: `pft_${imp.id}`,
    source: "pft-upload",
    importId: imp.id,
    file: imp.file || "",
    fileName: imp.fileName || "",
    ...(imp.pages ? { pages: imp.pages } : {}),
    nameOnReport: printed.name || "",
    dobOnReport: printed.dob || "",
  };
};

// What a person must check on an import: the job's list, plus identity
// problems found here. Each is { importId, field, reason, readings?, resolved }.
export const reviewItemsFor = (imp) => {
  const items = [];
  const seen = new Set();
  const add = (field, reason, readings) => {
    if (seen.has(field)) return;
    seen.add(field);
    items.push({
      importId: imp.id,
      field,
      reason,
      ...(readings ? { readings } : {}),
      resolved: !!(imp.reviewed && imp.reviewed[field]),
    });
  };
  // "Several patients in one PDF" is information, not a check (decided 2026-10-06); older
  // imports still carry it as a review item, so it is skipped here.
  for (const r of (imp.review || []).filter((x) => x.field !== "patients")) add(r.field, r.reason || "check", r.readings);
  const p = imp.patient || {};
  const mrn = normalizeMrn9(p.mrn);
  if (!p.mrn9 && mrn.issue) add("mrn", `MRN ${mrn.issue}`);
  if (blank(p.name)) add("name", "name missing");
  if (!normalizeDob(p.dob)) add("dob", blank(p.dob) ? "date of birth missing" : "date of birth unreadable");
  if (!normalizeDob(imp.test && imp.test.date)) add("date", "PFT date missing or unreadable");
  return items;
};

const mostCommon = (values) => {
  const counts = new Map();
  for (const v of values.filter((x) => !blank(x))) counts.set(v, (counts.get(v) || 0) + 1);
  let best = "";
  let n = 0;
  for (const [v, c] of counts) if (c > n) [best, n] = [v, c];
  return best;
};

/**
 * Two printed names that differ ONLY by a middle name (or initial): every word of the
 * shorter one is in the longer one, in any order ("SMITH, MIKE" = "MIKE FERRERO SMITH"),
 * a lone initial matching a word it starts ("MIKE F SMITH" = "MIKE FERRERO SMITH").
 * The shorter one needs at least two words. Decided 2026-10-27: not a "Same patient?".
 * Same rule as functions/pftSurvey.js middleNameOnly (the texting check).
 */
export const middleNameOnly = (a, b) => {
  const x = nameTokens(a);
  const y = nameTokens(b);
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  if (short.length < 2) return false;
  const left = [...long];
  for (const t of short) {
    const i = left.findIndex((w) => w === t || (t.length === 1 && w.startsWith(t)) || (w.length === 1 && t.startsWith(w)));
    if (i < 0) return false;
    left.splice(i, 1);
  }
  return [...x].sort().join(" ") !== [...y].sort().join(" "); // the same name is not this rule
};

/** Do these printed names all name the same person, apart from middle names? */
const namesOnlyDifferByMiddle = (names) => {
  const list = [...new Set(names.filter((n) => !blank(n)))];
  const keyed = new Map(list.map((n) => [nameTokens(n).sort().join(" "), n]));
  const distinct = [...keyed.values()];
  if (distinct.length < 2) return false;
  return distinct.every((a, i) => distinct.slice(i + 1).every((b) => middleNameOnly(a, b)));
};

/** The fullest of these printed names (most words, then most letters: "MARIE" over "M";
 *  ties: the one printed most often). */
const fullestName = (names) => {
  const size = (n) => nameTokens(n).length * 1000 + nameTokens(n).join("").length;
  const kept = names.filter((n) => !blank(n));
  const most = Math.max(...kept.map(size));
  return mostCommon(kept.filter((n) => size(n) === most));
};

const cardStatus = (imports) => {
  const s = new Set(imports.map((i) => i.status || "new"));
  if (s.has("new")) return "new";
  if (s.has("merged")) return "merged";
  if (s.has("moved")) return "moved";
  return "dismissed";
};

/**
 * Group import docs into patient cards. Returns cards in a stable order
 * (by name, then key). A card:
 *   { key, imports, pendingImports, name, dob, mrn9, sex, phone, address,
 *     tests (newest first), review, needsReview, status, identityDiffers }
 */
export const groupImportsIntoCards = (imports = []) => {
  const list = imports.filter((i) => i && i.id);
  // Union-find over import indexes.
  const parent = list.map((_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const mrnsOf = new Map(); // root -> Set of mrn9
  list.forEach((imp, i) => mrnsOf.set(i, new Set(importMrn9(imp) ? [importMrn9(imp)] : [])));
  // A person can say PDFs that were grouped together are NOT the same patient
  // ("Not the same patient: separate"): those imports get a `splitGroup`, and imports
  // with different splitGroups are never joined, whatever their MRN or name.
  const tagOf = new Map();
  list.forEach((imp, i) => tagOf.set(i, imp.splitGroup || ""));
  const union = (a, b, requireCompatible) => {
    const ra = find(a);
    const rb = find(b);
    if (ra === rb) return;
    if (tagOf.get(ra) !== tagOf.get(rb)) return; // separated by a person
    const merged = new Set([...mrnsOf.get(ra), ...mrnsOf.get(rb)]);
    if (requireCompatible && merged.size > 1) return; // different MRNs: different patients
    parent[rb] = ra;
    mrnsOf.set(ra, merged);
  };

  // Joined within each splitGroup: keyed by MRN (or name + DOB) AND the group, so an
  // import from another group earlier in the list can't stop the rest joining. Before
  // 2026-10-26 it could: one patient became several cards with the same key, and the
  // page then left an extra copy of the card behind on every tab switch.
  const byMrn = new Map();
  list.forEach((imp, i) => {
    const m = importMrn9(imp);
    if (!m) return;
    const k = `${m}\u0000${tagOf.get(i)}`;
    if (byMrn.has(k)) union(byMrn.get(k), i, false);
    else byMrn.set(k, i);
  });
  const byName = new Map();
  list.forEach((imp, i) => {
    const nk = nameDobKey(imp);
    if (!nk) return;
    const k = `${nk}\u0000${tagOf.get(i)}`;
    if (!byName.has(k)) byName.set(k, []);
    byName.get(k).push(i);
  });
  for (const idxs of byName.values()) {
    for (let j = 1; j < idxs.length; j++) union(idxs[0], idxs[j], true);
  }

  const groups = new Map();
  list.forEach((imp, i) => {
    const r = find(i);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(imp);
  });

  const cards = [];
  for (const group of groups.values()) {
    const pts = group.map((i) => i.patient || {});
    const mrns = [...new Set(group.map(importMrn9).filter(Boolean))];
    const names = new Set(pts.map((p) => nameTokens(p.name).sort().join(" ")).filter(Boolean));
    const dobs = new Set(pts.map((p) => normalizeDob(p.dob)).filter(Boolean));
    // PDFs that print the name with and without a middle name are not a "Same
    // patient?" (2026-10-27): the card uses the fuller name and says so quietly.
    const middleOnly = names.size > 1 && namesOnlyDifferByMiddle(pts.map((p) => p.name));
    const namesDiffer = names.size > 1 && !middleOnly;
    const identityDiffers = namesDiffer || dobs.size > 1;
    const review = group.flatMap(reviewItemsFor);
    if (identityDiffers) {
      // Card-level check: resolved only when a person ticked it on every import. It
      // lists each name / DOB found and which PDFs print it, and why they were put on
      // one card, so a person can check them, or separate them.
      const variants = new Map();
      for (const imp of group) {
        const p = imp.patient || {};
        const k = `${nameTokens(p.name).sort().join(" ")}|${normalizeDob(p.dob)}`;
        if (!variants.has(k)) variants.set(k, { name: p.name || "", dob: p.dob || "", importIds: [], files: [] });
        const v = variants.get(k);
        v.importIds.push(imp.id);
        v.files.push({ importId: imp.id, file: imp.file || "", fileName: imp.fileName || "", pages: imp.pages || "" });
      }
      const what = namesDiffer && dobs.size > 1 ? "names and dates of birth" : namesDiffer ? "names" : "dates of birth";
      review.push({
        importId: null,
        field: "identity",
        reason: `the ${what} differ between PDFs`,
        why: mrns.length === 1
          ? `These PDFs were put together because they share MRN ${mrns[0]}, but they print different ${what}.`
          : `These PDFs were put together because they share a name and date of birth, but they print different ${what}.`,
        variants: [...variants.values()],
        resolved: group.every((i) => i.reviewed && i.reviewed.identity),
        chosen: (group.find((i) => i.identityChoice) || {}).identityChoice || null,
      });
    }
    const pendingImports = group.filter((i) => (i.status || "new") === "new");
    // The name and DOB a person chose when confirming these PDFs are one patient; else
    // the one printed most often.
    const choice = identityDiffers ? (group.find((i) => i.identityChoice) || {}).identityChoice : null;
    const name =
      (choice && choice.name) || (middleOnly ? fullestName(pts.map((p) => p.name)) : mostCommon(pts.map((p) => p.name)));
    const dob = (choice && choice.dob) || mostCommon(pts.map((p) => p.dob));
    // A PDF that prints no DOB, on a card where another PDF does (2026-10-27): the card has
    // the patient's DOB, so it's no check. (An unreadable DOB still is.)
    if (normalizeDob(dob)) {
      for (const r of review) {
        const imp = r.field === "dob" && !r.resolved && group.find((i) => i.id === r.importId);
        if (imp && blank((imp.patient || {}).dob)) {
          r.resolved = true;
          r.autoResolved = "this PDF prints no date of birth; another PDF of this patient does";
        }
      }
    }
    const tag = (group[0] && group[0].splitGroup) || "";
    cards.push({
      // Unique even when separated cards share an MRN (the splitGroup tells them apart).
      key: `${mrns.length ? `mrn:${mrns[0]}` : `id:${group.map((i) => i.id).sort()[0]}`}${tag ? `:${tag}` : ""}`,
      imports: group,
      pendingImports,
      name,
      dob,
      mrn9: mrns[0] || "",
      mrnRaw: mostCommon(pts.map((p) => p.mrn)),
      sex: mostCommon(pts.map((p) => p.sex)),
      phone: mostCommon(pts.map((p) => p.phone)),
      address: mostCommon(pts.map((p) => p.address)),
      // Smoking status can change over time: the newest report that prints one wins,
      // mapped to a dropdown choice when clear, with the printed words as the note.
      ...(() => {
        const printed = (visibleTestsNewestFirst(group.map(importToTest)).find(
          (t) => t.smokingStatus && String(t.smokingStatus).trim(),
        ) || {}).smokingStatus;
        const { status, note } = parseSmoking(printed);
        return { smokingStatus: status, smokingNote: note };
      })(),
      tests: collapseSameReports(visibleTestsNewestFirst(group.map(importToTest))),
      review,
      // Only checks on imports still waiting to move block the move.
      needsReview: review.some(
        (r) => !r.resolved && (r.importId === null || pendingImports.some((p) => p.id === r.importId)),
      ),
      identityDiffers,
      // The other ways this name was printed or read, when they differ only by a middle
      // name: across PDFs, or one PDF's other reading (patient.nameAlsoRead, set by the
      // extraction). A quiet note, never a check.
      otherNamesPrinted: [
        ...new Set(
          [...(middleOnly ? pts.map((p) => p.name) : []), ...pts.map((p) => p.nameAlsoRead)].filter(
            (n) => !blank(n) && nameTokens(n).sort().join(" ") !== nameTokens(name).sort().join(" "),
          ),
        ),
      ],
      status: cardStatus(group),
      // Health-survey text (pftSurvey.js): null until the patient is texted.
      survey: surveyOf(group),
    });
  }
  // Different cards with the same MRN (separated by a person, or different names and
  // DOBs): one MRN is probably wrong, so each card says so.
  const byMrn9 = new Map();
  for (const c of cards) {
    if (!c.mrn9) continue;
    if (!byMrn9.has(c.mrn9)) byMrn9.set(c.mrn9, []);
    byMrn9.get(c.mrn9).push(c);
  }
  for (const c of cards) {
    c.sameMrnCards = (c.mrn9 ? byMrn9.get(c.mrn9) : [])
      .filter((o) => o !== c)
      .map((o) => ({ key: o.key, name: o.name, status: o.status }));
  }
  // Values only ONE reading found and nobody verified yet (2026-10-27): the card is outlined
  // and says how many, but nothing is held back.
  for (const c of cards) {
    c.oneReadingToRecheck = c.tests.reduce(
      (n, t) =>
        n + softChecksOf(t, { patientAddress: c.address, patientDob: c.dob }).filter((s) => s.oneReading && !s.verified).length,
      0,
    );
  }
  // A PDF holding several patients: each of their cards links it (every test keeps its
  // file) and says how many other patients' reports are in it.
  const cardsByFile = new Map();
  for (const c of cards) {
    for (const i of c.imports) {
      if (!cardsByFile.has(i.file)) cardsByFile.set(i.file, new Set());
      cardsByFile.get(i.file).add(c.key);
    }
  }
  for (const c of cards) {
    c.sharedPdfs = [...new Set(c.imports.map((i) => i.file))]
      .filter((f) => cardsByFile.get(f).size > 1)
      .map((f) => ({
        file: f,
        fileName: (c.imports.find((i) => i.file === f) || {}).fileName || "",
        otherPatients: cardsByFile.get(f).size - 1,
      }));
  }
  return cards.sort((a, b) => (a.name || "").localeCompare(b.name || "") || a.key.localeCompare(b.key));
};

// ---- PFT Uploads page list: "Needs review" filter and pages of 51 ----

// 51 = 17 full rows of the page's 3-column grid.
export const PFT_PAGE_SIZE = 51;

/** Cards with something still to check (the "Needs review" filter on the New tab). */
export const needsReviewCards = (cards = []) => cards.filter((c) => c && c.needsReview);

const sameNameOrMiddle = (a, b) =>
  !!nameTokens(a).length && (nameTokens(a).sort().join(" ") === nameTokens(b).sort().join(" ") || middleNameOnly(a, b));

/**
 * No PDF of the card prints a DOB, but its MRN is a patient already in RegX (decided
 * 2026-10-27). The "DOB missing" checks are settled ONLY when every RegX record with
 * exactly this MRN has the same name (or only a middle name differs) and one of them has
 * a DOB: a misread MRN digit could otherwise point at another person. A different name
 * keeps the check and says why. `candidates`: loadMatchCandidates() (consents and, at
 * Dubois, the Sheet; never Duplicates). Returns new card objects.
 */
export const settleDobFromRegx = (cards = [], candidates = null) => {
  if (!Array.isArray(candidates) || !candidates.length) return cards;
  return cards.map((c) => {
    if (!c || normalizeDob(c.dob) || !c.mrn9) return c;
    const missing = (r) =>
      r.field === "dob" && !r.resolved && blank(((c.imports.find((i) => i.id === r.importId) || {}).patient || {}).dob);
    if (!c.review.some(missing)) return c;
    const same = candidates.filter((p) => p && sameMrnNumber(p.mrn, c.mrn9));
    if (!same.length) return c;
    const nameOk = same.every((p) => sameNameOrMiddle(c.name, p.name || p.fullName));
    const withDob = nameOk ? same.find((p) => normalizeDob(p.dob)) : null;
    const review = c.review.map((r) => {
      if (!missing(r)) return r;
      if (withDob) {
        return { ...r, resolved: true, autoResolved: "no PDF prints a date of birth; the RegX patient with this MRN and name has one" };
      }
      return nameOk ? r : { ...r, reason: "date of birth missing; a RegX patient has this MRN but a different name" };
    });
    return {
      ...c,
      review,
      needsReview: review.some(
        (r) => !r.resolved && (r.importId === null || c.pendingImports.some((p) => p.id === r.importId)),
      ),
      dobFromRegx: withDob ? { dob: withDob.dob, id: withDob.id, store: withDob.store } : null,
    };
  });
};

/** Cards with nothing left to check: every review item ticked, or none to begin with. */
export const readyToMoveCards = (cards = []) => cards.filter((c) => c && !c.needsReview);

/**
 * One page of a list. `page` is 1-based and is kept in range, so a page that no
 * longer exists (cards moved away, a narrower search) shows the last one instead.
 * Returns { items, page, pageCount, start, end, total } (start/end 1-based, 0 when empty).
 */
export const pageOfList = (list = [], page = 1, size = PFT_PAGE_SIZE) => {
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const p = Math.min(Math.max(1, Math.floor(Number(page) || 1)), pageCount);
  const from = (p - 1) * size;
  const items = list.slice(from, from + size);
  return { items, page: p, pageCount, start: total ? from + 1 : 0, end: from + items.length, total };
};
